/**
 * Sales Module API Service
 *
 * API functions for prospects, commissions, dashboard, and territory availability.
 */
import api from './api'
import type {
  Prospect,
  ProspectDetail,
  ProspectListItem,
  ProspectNote,
  ProspectStatus,
  ProspectService,
  ProspectSource,
  TradeSlot,
  TerritorySalesAvailability,
  RegionTerritories,
  SalesDashboard,
  CommissionRecord,
  ClientPayment,
  PayrollMonthSummary,
  SalespersonPayrollDetail,
  AdminSalesOverview,
} from '@/types'

// ============================================================================
// Prospects API
// ============================================================================

export interface ProspectFilters {
  page?: number
  pageSize?: number
  territoryId?: number
  status?: ProspectStatus
  services?: ProspectService
  tradeSlot?: TradeSlot
  socialMediaAdvertising?: boolean
  followUpOverdue?: boolean
  search?: string
  assignedToId?: number
}

export interface CreateProspectData {
  companyName: string
  contactName: string
  contactPhone: string
  contactEmail?: string
  address?: string
  website?: string
  territoryId?: number
  tradeSlot?: TradeSlot
  services: ProspectService
  socialMediaAdvertising?: boolean
  nextFollowUpDate?: string
  source?: ProspectSource
  sourceNotes?: string
  initialNote?: string
}

export interface UpdateProspectData {
  companyName?: string
  contactName?: string
  contactPhone?: string
  contactEmail?: string
  address?: string
  website?: string
  services?: ProspectService
  socialMediaAdvertising?: boolean
  nextFollowUpDate?: string
  territoryId?: number
  tradeSlot?: TradeSlot
}

/**
 * List prospects with filtering and pagination
 */
export const getProspects = async (filters?: ProspectFilters): Promise<ProspectListItem[]> => {
  const params = filters ? {
    page: filters.page,
    page_size: filters.pageSize,
    territory_id: filters.territoryId,
    status: filters.status,
    services: filters.services,
    trade_slot: filters.tradeSlot,
    social_media_advertising: filters.socialMediaAdvertising,
    follow_up_overdue: filters.followUpOverdue,
    search: filters.search,
    assigned_to_id: filters.assignedToId,
  } : undefined

  const response = await api.get('/api/v1/sales/prospects/', { params })
  return response.data
}

/**
 * Get a single prospect with full details and notes
 */
export const getProspect = async (id: number): Promise<ProspectDetail> => {
  const response = await api.get(`/api/v1/sales/prospects/${id}`)
  return response.data
}

/**
 * Create a new prospect
 */
export const createProspect = async (data: CreateProspectData): Promise<Prospect> => {
  const payload = {
    company_name: data.companyName,
    contact_name: data.contactName,
    contact_phone: data.contactPhone,
    contact_email: data.contactEmail,
    address: data.address,
    website: data.website,
    territory_id: data.territoryId,
    trade_slot: data.tradeSlot,
    services: data.services,
    social_media_advertising: data.socialMediaAdvertising,
    next_follow_up_date: data.nextFollowUpDate,
    source: data.source,
    source_notes: data.sourceNotes,
    initial_note: data.initialNote,
  }

  const response = await api.post('/api/v1/sales/prospects/', payload)
  return response.data
}

/**
 * Update a prospect
 */
export const updateProspect = async (id: number, data: UpdateProspectData): Promise<Prospect> => {
  const payload: Record<string, unknown> = {}

  if (data.companyName !== undefined) payload.company_name = data.companyName
  if (data.contactName !== undefined) payload.contact_name = data.contactName
  if (data.contactPhone !== undefined) payload.contact_phone = data.contactPhone
  if (data.contactEmail !== undefined) payload.contact_email = data.contactEmail
  if (data.address !== undefined) payload.address = data.address
  if (data.website !== undefined) payload.website = data.website
  if (data.services !== undefined) payload.services = data.services
  if (data.socialMediaAdvertising !== undefined) payload.social_media_advertising = data.socialMediaAdvertising
  if (data.nextFollowUpDate !== undefined) payload.next_follow_up_date = data.nextFollowUpDate
  if (data.territoryId !== undefined) payload.territory_id = data.territoryId
  if (data.tradeSlot !== undefined) payload.trade_slot = data.tradeSlot

  const response = await api.patch(`/api/v1/sales/prospects/${id}`, payload)
  return response.data
}

/**
 * Update prospect pipeline status
 */
export const updateProspectStatus = async (
  id: number,
  status: ProspectStatus,
  note?: string
): Promise<Prospect> => {
  const response = await api.post(`/api/v1/sales/prospects/${id}/status`, {
    status,
    note,
  })
  return response.data
}

/**
 * Mark prospect as won
 */
export const markProspectWon = async (
  id: number,
  servicesConfirmed: ProspectService,
  note?: string
): Promise<Prospect> => {
  const response = await api.post(`/api/v1/sales/prospects/${id}/won`, {
    services_confirmed: servicesConfirmed,
    note,
  })
  return response.data
}

/**
 * Add a note to prospect timeline
 */
export const addProspectNote = async (prospectId: number, content: string): Promise<ProspectNote> => {
  const response = await api.post(`/api/v1/sales/prospects/${prospectId}/notes`, { content })
  return response.data
}

/**
 * Get prospect notes
 */
export const getProspectNotes = async (prospectId: number): Promise<ProspectNote[]> => {
  const response = await api.get(`/api/v1/sales/prospects/${prospectId}/notes`)
  return response.data
}

/**
 * Get won prospects queue (admin)
 */
export const getWonProspectsQueue = async (): Promise<ProspectListItem[]> => {
  const response = await api.get('/api/v1/sales/prospects/won/queue')
  return response.data
}

/**
 * Bulk import prospects from CSV file
 */
export interface BulkImportResult {
  totalRows: number
  imported: number
  skipped: number
  errors: string[]
}

export const bulkImportProspects = async (
  file: File,
  source: ProspectSource
): Promise<BulkImportResult> => {
  const formData = new FormData()
  formData.append('file', file)

  // Remove default Content-Type header so axios can set multipart/form-data with boundary
  const response = await api.post('/api/v1/sales/prospects/bulk-import', formData, {
    headers: {
      'Content-Type': undefined,
    },
    params: {
      source,
    },
  })
  return response.data
}

// ============================================================================
// Territory Availability API
// ============================================================================

/**
 * Get territory availability (slot status)
 */
export const getTerritoryAvailability = async (
  region?: string,
  availableOnly?: boolean
): Promise<TerritorySalesAvailability[]> => {
  const response = await api.get('/api/v1/territories/availability', {
    params: { region, available_only: availableOnly },
  })
  return response.data
}

/**
 * Get territory availability grouped by region
 */
export const getTerritoryAvailabilityByRegion = async (): Promise<RegionTerritories[]> => {
  const response = await api.get('/api/v1/territories/availability/by-region')
  return response.data
}

// ============================================================================
// Dashboard API
// ============================================================================

/**
 * Get sales dashboard data
 */
export const getSalesDashboard = async (): Promise<SalesDashboard> => {
  const response = await api.get('/api/v1/sales/dashboard/')
  return response.data
}

/**
 * Get admin sales overview
 */
export const getAdminSalesOverview = async (
  month?: number,
  year?: number
): Promise<AdminSalesOverview> => {
  const response = await api.get('/api/v1/sales/dashboard/admin-overview', {
    params: { month, year },
  })
  return response.data
}

// ============================================================================
// Commission API (Salesperson View)
// ============================================================================

/**
 * Get current user's commission records
 */
export const getMyCommission = async (
  month?: number,
  year?: number,
  statusFilter?: string
): Promise<CommissionRecord[]> => {
  const response = await api.get('/api/v1/sales/commission/my-commission', {
    params: { month, year, status_filter: statusFilter },
  })
  return response.data
}

/**
 * Get current user's commission summary for a month
 */
export const getMyCommissionSummary = async (
  month: number,
  year: number
): Promise<{
  periodMonth: number
  periodYear: number
  totalEarned: number
  pendingClientPayment: number
  readyToPay: number
  paid: number
  deferred: number
  recordCount: number
}> => {
  const response = await api.get('/api/v1/sales/commission/my-commission/summary', {
    params: { month, year },
  })
  return response.data
}

// ============================================================================
// Payroll API (Admin Only)
// ============================================================================

/**
 * Get payroll summary for a month
 */
export const getPayrollSummary = async (month: number, year: number): Promise<PayrollMonthSummary> => {
  const response = await api.get('/api/v1/sales/commission/payroll', {
    params: { month, year },
  })
  return response.data
}

/**
 * Get detailed payroll for a specific salesperson
 */
export const getSalespersonPayrollDetail = async (
  salespersonId: number,
  month: number,
  year: number
): Promise<SalespersonPayrollDetail> => {
  const response = await api.get(`/api/v1/sales/commission/payroll/${salespersonId}`, {
    params: { month, year },
  })
  return response.data
}

/**
 * Mark commissions as paid
 */
export const markCommissionsPaid = async (commissionIds: number[]): Promise<{
  message: string
  updatedCount: number
}> => {
  const response = await api.post('/api/v1/sales/commission/payroll/mark-paid', {
    commission_ids: commissionIds,
  })
  return response.data
}

/**
 * Defer commissions to a future month
 */
export const deferCommissions = async (
  commissionIds: number[],
  deferredToMonth: number,
  deferredToYear: number
): Promise<{
  message: string
  updatedCount: number
}> => {
  const response = await api.post('/api/v1/sales/commission/payroll/defer', {
    commission_ids: commissionIds,
    deferred_to_month: deferredToMonth,
    deferred_to_year: deferredToYear,
  })
  return response.data
}

// ============================================================================
// Client Payment API (Admin Only)
// ============================================================================

/**
 * Get client payment records for a month
 */
export const getClientPayments = async (
  month: number,
  year: number,
  paymentReceived?: boolean
): Promise<ClientPayment[]> => {
  const response = await api.get('/api/v1/sales/commission/client-payments', {
    params: { month, year, payment_received: paymentReceived },
  })
  return response.data
}

/**
 * Update a client payment record
 */
export const updateClientPayment = async (
  paymentId: number,
  paymentReceived: boolean,
  notes?: string
): Promise<ClientPayment> => {
  const response = await api.patch(`/api/v1/sales/commission/client-payments/${paymentId}`, {
    payment_received: paymentReceived,
    notes,
  })
  return response.data
}

/**
 * Bulk update client payments
 */
export const bulkUpdateClientPayments = async (
  paymentIds: number[],
  paymentReceived: boolean
): Promise<{
  message: string
  updatedCount: number
}> => {
  const response = await api.post('/api/v1/sales/commission/client-payments/bulk-update', {
    payment_ids: paymentIds,
    payment_received: paymentReceived,
  })
  return response.data
}
